import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { FirestoreEventBusService } from 'src/app/shared/services/firestore-event-bus.service';
import { ChunkService } from 'src/app/views/content/chunk/chunk.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ExportService } from 'src/app/shared/services/export.service';

@Component({
  selector: 'app-export-status-popover',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, TranslateModule, MatProgressSpinnerModule, UpperCasePipe],
  template: `
    <div class="credit-alert-popover">
      <button type="button" mat-icon-button (click)="close($event)" class="close-btn">
        <mat-icon>close</mat-icon>
      </button>
      @if (status()?.state === 'generating') {
        <div class="popover-header">
          <mat-progress-spinner mode="indeterminate" [diameter]="18" class="popover-spinner"></mat-progress-spinner>
          <span class="popover-title">{{ 'Export.Generating' | translate }}</span>
        </div>
        <p class="popover-message">{{ 'Export.GeneratingMessage' | translate: { type: (status()?.type || '' | uppercase) } }}</p>
      } @else if (status()?.state === 'done') {
        <div class="popover-header">
          <mat-icon class="popover-icon success">check_circle</mat-icon>
          <span class="popover-title">{{ 'Export.Done' | translate }}</span>
        </div>
        <p class="popover-message">{{ 'Export.DoneMessage' | translate }}</p>
        <a mat-flat-button color="primary" class="popover-btn" [href]="status()?.downloadUrl" target="_blank" (click)="close($event)">
          <mat-icon>file_download</mat-icon>
          {{ 'Export.Download' | translate }}
        </a>
      } @else if (status()?.state === 'error') {
        <div class="popover-header">
          <mat-icon class="popover-icon error">error</mat-icon>
          <span class="popover-title">{{ 'Export.Error' | translate }}</span>
        </div>
        <p class="popover-message">{{ 'Export.ErrorMessage' | translate }}</p>
        <button type="button" mat-button class="popover-btn" (click)="close($event)">
          {{ 'Global.Close' | translate }}
        </button>
      }
    </div>
  `,
  styles: [
    `
      .credit-alert-popover {
        position: relative;
        padding: 16px;
        width: 240px;
        background: var(--skz-background-color-surface, #ffffff);
        border: 1px solid var(--skz-border-color-main, rgba(226, 232, 240, 0.8));
        border-radius: var(--skz-radius-medium, 12px);
        box-shadow: var(--skz-box-shadow-light, 0 4px 12px rgba(0, 0, 0, 0.08));
        animation: popover-enter 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
      }

      .close-btn {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 24px !important;
        height: 24px !important;
        line-height: 24px !important;
        padding: 0 !important;
        min-width: 0 !important;
        color: var(--skz-text-muted, #64748b);
        opacity: 0.6;
        transition: opacity 0.2s;
        &:hover {
          opacity: 1;
        }
        mat-icon {
          font-size: 16px !important;
          width: 16px !important;
          height: 16px !important;
          line-height: 16px !important;
        }
      }

      @keyframes popover-enter {
        from {
          opacity: 0;
          transform: scale(0.92) translateY(6px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }

      .popover-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
      }

      .popover-spinner {
        ::ng-deep circle {
          stroke: var(--primary, #5a73c5);
        }
      }

      .popover-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;

        &.success {
          color: #10b981;
        }

        &.error {
          color: #ef4444;
        }
      }

      .popover-title {
        font-size: 13px;
        font-weight: 700;
        color: var(--skz-text-primary, #0f172a);
      }

      .popover-message {
        font-size: 12px;
        line-height: 1.5;
        color: var(--skz-text-muted, #64748b);
        margin: 0 0 12px;
      }

      .popover-btn {
        width: 100%;
        font-size: 12px;
        font-weight: 600;
        border-radius: var(--skz-radius-low, 8px);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;

        mat-icon {
          font-size: 16px;
          width: 16px;
          height: 16px;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportStatusPopoverComponent implements OnDestroy {
  private exportService = inject(ExportService);
  private overlayService = inject(DynamicOverlayService);
  private firestoreEventBus = inject(FirestoreEventBusService);
  private authService = inject(AuthService);
  private chunkService = inject(ChunkService);

  status = this.exportService.exportStatus;
  private taskSub: Subscription | null = null;

  constructor() {
    effect(() => {
      const status = this.status();
      if (status?.state === 'generating' && status?.contentId) {
        this.startListening(status.contentId, status.type, status.downloadId);
      } else if (status?.state === 'done' && status?.downloadUrl) {
        this.download();
      } else if (!status || status.state !== 'generating') {
        this.stopListening();
      }
    });
  }

  private startListening(contentId: string, format: 'docx' | 'pdf' | 'markdown', downloadId?: string) {
    const tenantId = localStorage.getItem('active_workspace') || this.authService.userInfo()?._id;
    if (!tenantId) {
      return;
    }

    this.stopListening();

    this.taskSub = this.firestoreEventBus.listenToContentStatus(tenantId, contentId).subscribe({
      next: (docData) => {
        const taskKey = downloadId ? `export_${downloadId}` : 'export';
        const exportTask = docData?.activeTasks?.[format]?.[taskKey];
        if (!exportTask) {
          return;
        }

        if (exportTask.status === 'completed' && exportTask.url) {
          this.stopListening();
          this.chunkService.getDownloadURL(exportTask.url).then((downloadUrl) => {
            this.exportService.exportStatus.set({
              state: 'done',
              type: format,
              downloadUrl
            });
          }).catch((err) => {
            console.error(`Error resolving GCS URL in popover for ${format}:`, err);
            this.exportService.exportStatus.set({
              state: 'error',
              type: format
            });
          });
        } else if (exportTask.status === 'failed') {
          this.stopListening();
          this.exportService.exportStatus.set({
            state: 'error',
            type: format
          });
        }
      },
      error: (err) => {
        console.error('Error listening to export task status:', err);
        this.stopListening();
        this.exportService.exportStatus.set({
          state: 'error',
          type: format
        });
      }
    });
  }

  private stopListening() {
    if (this.taskSub) {
      this.taskSub.unsubscribe();
      this.taskSub = null;
    }
  }

  ngOnDestroy() {
    this.stopListening();
  }

  download() {
    const status = this.status();
    if (status?.downloadUrl) {
      try {
        const link = document.createElement('a');
        link.href = status.downloadUrl;
        link.setAttribute('download', '');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.close();
      } catch (err) {
        console.error('Failed to trigger auto-download via anchor:', err);
        const win = window.open(status.downloadUrl, '_blank');
        if (win) {
          this.close();
        } else {
          console.warn('Popup blocked or failed to open. Keeping download popover visible.');
        }
      }
    }
  }

  close(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.overlayService.close();
    this.exportService.exportStatus.set(null);
  }
}
